import { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [seller, setSeller] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchSeller(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchSeller(session.user.id)
      else { setSeller(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  const fetchSeller = async (userId) => {
    const { data } = await supabase
      .from('sellers')
      .select('*')
      .eq('user_id', userId)
      .single()
    setSeller(data)
    setLoading(false)
  }

  const signUp = async ({ email, password, shopName, phone, facebookUrl, sellerMode, currency, country }) => {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) return { error }

    const trialEndsAt = new Date()
    trialEndsAt.setDate(trialEndsAt.getDate() + 14)

    await supabase.from('sellers').insert({
      user_id: data.user.id,
      email,
      shop_name: shopName,
      phone,
      facebook_url: facebookUrl,
      seller_mode: sellerMode || 'bangladesh',
      currency: currency || 'BDT',
      country: country || null,
      plan: 'trial',
      subscription_plan: 'trial',
      trial_ends_at: trialEndsAt.toISOString(),
    })

    await fetchSeller(data.user.id)
    return { data }
  }

  const signIn = async ({ email, password }) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
    setSeller(null)
  }

  // Trial helpers
  const trialDaysLeft = () => {
    if (!seller?.trial_ends_at) return null
    if (seller?.plan !== 'trial') return null
    const diff = new Date(seller.trial_ends_at) - new Date()
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
  }

  const isTrialExpired = () => {
    if (seller?.plan !== 'trial') return false
    const days = trialDaysLeft()
    return days !== null && days <= 0
  }

  // Subscription plan
  const getSubscriptionPlan = () => {
    if (seller?.suspended) return 'suspended'
    return seller?.subscription_plan || seller?.plan || 'trial'
  }

  const PLAN_LABELS = {
    trial: 'Free Trial',
    starter: 'Starter',
    growth: 'Growth',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Suspended',
  }

  const PLAN_COLORS = {
    trial: 'text-amber-600',
    starter: 'text-gray-600',
    growth: 'text-blue-600',
    pro: 'text-purple-600',
    business: 'text-amber-700',
    suspended: 'text-red-600',
  }

  const getPlanLabel = () => PLAN_LABELS[getSubscriptionPlan()] || 'Unknown'
  const getPlanColor = () => PLAN_COLORS[getSubscriptionPlan()] || 'text-gray-600'

  // Days left on paid subscription or trial
  const subscriptionDaysLeft = () => {
    const plan = getSubscriptionPlan()
    if (plan === 'trial') return trialDaysLeft()
    if (!seller?.subscription_end) return null
    const diff = new Date(seller.subscription_end) - new Date()
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
  }

  const isSubscriptionExpired = () => {
    const plan = getSubscriptionPlan()
    if (plan === 'suspended') return true
    if (plan === 'trial') return isTrialExpired()
    const days = subscriptionDaysLeft()
    return days !== null && days <= 0
  }

  // Feature gating
  const PLAN_LEVEL = {
    suspended: -1,
    trial: 4,
    starter: 1,
    growth: 2,
    pro: 3,
    business: 4,
  }

  const FEATURE_PLAN = {
    // All paid plans — starter and above
    orders: 1,
    inventory: 1,
    analytics_basic: 1,
    website: 1,
    customers: 1,
    finance: 1,
    // Growth and above
    analytics_full: 2,
    goals: 2,
    receipts: 2,
    // Pro and above
    events: 3,
    export: 3,
    priority_support: 3,
    // Business only
    staff_accounts: 4,
    dedicated_support: 4,
  }

  const isFeatureAllowed = (feature) => {
    if (seller?.suspended) return false
    const plan = getSubscriptionPlan()
    if (isSubscriptionExpired() && plan !== 'trial') return false
    const userLevel = PLAN_LEVEL[plan] ?? 0
    const required = FEATURE_PLAN[feature] ?? 1
    return userLevel >= required
  }

  const isInternational = () => seller?.seller_mode === 'international'
  const getCurrency = () => seller?.currency || 'BDT'

  const formatAmount = (n) => {
    const amount = Math.round(n || 0).toLocaleString()
    return seller?.currency === 'USD' ? '$' + amount : '৳' + amount
  }

  return (
    <AuthContext.Provider value={{
      user, seller, loading,
      signUp, signIn, signOut,
      fetchSeller,
      trialDaysLeft, isTrialExpired,
      getSubscriptionPlan, getPlanLabel, getPlanColor,
      subscriptionDaysLeft, isSubscriptionExpired,
      isFeatureAllowed,
      isInternational, getCurrency, formatAmount,
      PLAN_LABELS, PLAN_COLORS,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)